import React, { useEffect, useRef, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import firebase from './firebase';
import { SCREENS, trackScreen } from './analytics';
import { getSession, logout, updateSession } from './auth';
import { api } from './api';
import PfpCropper from './PfpCropper';
import { isDarkMode, setDarkMode } from './darkMode';
import { EXPERIMENTS, getExperiments, setExperiment } from './experiments';
import { SOCIALS, readSocials, socialLinks } from './socials';
import { isPresenceEnabled, setPresenceEnabled, subscribePresence } from './discordPresence';
import { MAX_NAME, createBand, loadUserBands } from './bands';
import { InstagramIcon, SpotifyIcon, GlobeIcon } from './icons';

const BANNER_HEIGHT = 180;
const AVATAR_SIZE = 110;

const SOCIAL_ICONS = { instagram: InstagramIcon, spotify: SpotifyIcon, website: GlobeIcon };

// `username` is whose profile to show; omitted (or your own) means the editable "my profile" page.
// Anyone else's profile is read-only.
const Profile = ({ username: viewed }) => {
  const session = getSession();
  const username = viewed || session.username;
  const isSelf = username === session.username;
  const userRef = firebase.database().ref('users').child(username);
  const history = useHistory();

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', bio: '', instagram: '', spotify: '', website: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [imageBust, setImageBust] = useState('');
  const [cropFile, setCropFile] = useState(null);
  const [signedOut, setSignedOut] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', password: '' });
  const [emailSaving, setEmailSaving] = useState(false);
  const [dark, setDark] = useState(isDarkMode);
  const [discordOn, setDiscordOn] = useState(isPresenceEnabled);
  const [discordStatus, setDiscordStatus] = useState('off');
  useEffect(() => subscribePresence(setDiscordStatus), []);
  const [experiments, setExperiments] = useState(getExperiments);
  const [bands, setBands] = useState(null);
  const [newBand, setNewBand] = useState('');
  const [creatingBand, setCreatingBand] = useState(false);
  const pfpInput = useRef(null);
  const bannerInput = useRef(null);

  useEffect(() => { trackScreen(SCREENS.profile); }, []);

  useEffect(() => {
    userRef.once('value').then((snap) => {
      const v = snap.val() || {};
      setProfile({
        exists: snap.exists(),
        name: v.name || '',
        bio: v.bio || '',
        socials: readSocials(v),
        pfpURL: v.pfpURL || '',
        bannerURL: v.bannerURL || '',
      });
    }).catch(() => toast.error("Couldn't load profile ❌"));
    loadUserBands(username).then(setBands).catch(() => setBands([]));
    if (isSelf) api('email-status', { username }).then(setEmailStatus).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (signedOut) return <Redirect to="/login" />;
  if (!profile) {
    return <div className="text-center"><Spinner animation="border" /></div>;
  }
  if (!profile.exists) {
    return (
      <div className="text-center px-3 py-5">
        <p className="text-muted">We couldn't find that profile.</p>
        <Link to="/user">← Albums</Link>
      </div>
    );
  }

  const startEditing = () => {
    const inputs = {};
    SOCIALS.forEach((x) => { inputs[x.id] = x.toInput(profile.socials[x.id]); });
    setDraft({ name: profile.name, bio: profile.bio, ...inputs });
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const name = draft.name.trim();
    const bio = draft.bio.trim();
    if (!name) return toast.error("Display name can't be empty");

    // Each social: blank removes it, untouched keeps what's stored, otherwise it must validate.
    // The website keeps the text as typed (iOS reads the same `link` field); the others store the clean URL.
    const socials = {};
    for (const x of SOCIALS) {
      const typed = (draft[x.id] || '').trim();
      const stored = profile.socials[x.id];
      if (!typed) socials[x.id] = '';
      else if (typed === x.toInput(stored).trim()) socials[x.id] = stored;
      else {
        const url = x.parse(typed);
        if (!url) return toast.error(x.error);
        socials[x.id] = x.id === 'website' ? typed : url;
      }
    }

    // Only write fields that changed; an empty value removes the field.
    const updates = {};
    if (name !== profile.name) updates.name = name;
    if (bio !== profile.bio) updates.bio = bio || null;
    SOCIALS.forEach((x) => {
      if (socials[x.id] !== profile.socials[x.id]) updates[x.field || `socials/${x.id}`] = socials[x.id] || null;
    });

    setSaving(true);
    try {
      if (Object.keys(updates).length) await userRef.update(updates);
    } catch (err) {
      setSaving(false);
      return toast.error("Couldn't save profile ❌");
    }
    setSaving(false);
    setProfile({ ...profile, name, bio, socials });
    if (updates.name) updateSession({ name });
    setEditing(false);
    toast.success("Profile saved ✅");
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error("Please choose an image file");
    const isBanner = kind === 'banner';
    const field = isBanner ? 'bannerURL' : 'pfpURL';
    setUploading(kind);
    try {
      const ref = firebase.storage().ref().child(isBanner ? `images/banner${username}.jpg` : `images/${username}.jpg`);
      await ref.put(file, { contentType: file.type });
      const url = await ref.getDownloadURL();
      await userRef.child(field).set(url);
      setProfile((prev) => ({ ...prev, [field]: url }));
      setImageBust(`&t=${Date.now()}`);
      toast.success("Photo updated ✅");
    } catch (err) {
      toast.error("Couldn't upload photo ❌");
      setUploading(null);
      return false;
    }
    setUploading(null);
    return true;
  };

  const savePfp = async (blob) => {
    if (await uploadImage('pfp', blob)) setCropFile(null);
  };

  // Needs the password: otherwise anyone could attach their own email to someone else's account.
  const saveEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    try {
      const result = await api('set-email', { username, password: emailForm.password, email: emailForm.email.trim() });
      setShowEmail(false);
      if (result.alreadyVerified) {
        toast.success("That email is already verified ✅");
      } else {
        setEmailStatus((prev) => ({ ...prev, pending: true }));
        toast.success("Verification email sent 📧");
      }
    } catch (err) {
      toast.error(err.message);
    }
    setEmailSaving(false);
  };

  const signOut = () => {
    if (!window.confirm("Are you sure you want to sign out?")) return;
    logout();
    setSignedOut(true);
  };

  // Same as iOS: removes the user record only.
  const deleteAccount = async () => {
    try {
      await userRef.remove();
    } catch (err) {
      return toast.error("Couldn't delete account ❌");
    }
    logout();
    setSignedOut(true);
  };

  // A new band starts with you as its only member; add the others from its page.
  const startBand = async (e) => {
    e.preventDefault();
    if (!newBand.trim() || creatingBand) return;
    setCreatingBand(true);
    try {
      const id = await createBand(username, newBand);
      history.push(`/b/${encodeURIComponent(id)}`);
    } catch (err) {
      setCreatingBand(false);
      toast.error("Couldn't create band ❌");
    }
  };

  const imageSrc = (url) => (url ? url + imageBust : '');
  const links = socialLinks(profile.socials);
  const editableImageStyle = editing ? { outline: '2px solid #0d6efd', cursor: 'pointer' } : {};

  return (
    <div style={{ maxWidth: 640, width: '100%', margin: '0 auto', paddingBottom: 40 }}>
      <div className="px-3 py-2"><Link to="/user">← Albums</Link></div>

      <div
        onClick={() => editing && bannerInput.current.click()}
        style={{
          height: BANNER_HEIGHT,
          background: profile.bannerURL ? `center / cover no-repeat url("${imageSrc(profile.bannerURL)}")` : 'var(--placeholder-bg)',
          position: 'relative',
          ...editableImageStyle,
        }}
      >
        {uploading === 'banner' && <Spinner animation="border" style={{ position: 'absolute', top: 12, right: 12 }} />}
        {editing && <small style={{ position: 'absolute', bottom: 8, right: 12, color: '#fff', textShadow: '0 0 4px #000' }}>Change banner</small>}
      </div>
      <input ref={bannerInput} type="file" accept="image/*" hidden
        onChange={(e) => { uploadImage('banner', e.target.files[0]); e.target.value = ''; }} />

      <div className="px-3">
        <div
          onClick={() => editing && pfpInput.current.click()}
          style={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            marginTop: -AVATAR_SIZE / 2,
            borderRadius: '50%',
            border: '4px solid var(--avatar-border)',
            background: profile.pfpURL ? `center / cover no-repeat url("${imageSrc(profile.pfpURL)}")` : '#888',
            position: 'relative',
            ...editableImageStyle,
          }}
        >
          {uploading === 'pfp' && <Spinner animation="border" style={{ position: 'absolute', top: '35%', left: '35%' }} />}
        </div>
        <input ref={pfpInput} type="file" accept="image/*" hidden
          onChange={(e) => { if (e.target.files[0]) setCropFile(e.target.files[0]); e.target.value = ''; }} />

        {editing ? (
          <Form onSubmit={save} className="mt-3">
            <Form.Group controlId="profile-name">
              <Form.Label>Display name</Form.Label>
              <Form.Control value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Form.Group>
            <Form.Group controlId="profile-bio">
              <Form.Label>Bio</Form.Label>
              <Form.Control as="textarea" rows={3} value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} />
            </Form.Group>
            {SOCIALS.map((x) => (
              <Form.Group key={x.id} controlId={`profile-${x.id}`}>
                <Form.Label>{x.label}</Form.Label>
                <Form.Control placeholder={x.placeholder} autoCapitalize="none" autoCorrect="off"
                  value={draft[x.id] || ''} onChange={(e) => setDraft({ ...draft, [x.id]: e.target.value })} />
              </Form.Group>
            ))}
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>{' '}
            <Button variant="secondary" disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
          </Form>
        ) : (
          <div className="mt-3">
            <h3 className="mb-0">{profile.name || username}</h3>
            <div className="text-muted mb-3">@{username}</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>{profile.bio || <span className="text-muted">No bio</span>}</p>
            {links.length > 0 ? (
              <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
                {links.map((x) => {
                  const SocialIcon = SOCIAL_ICONS[x.id];
                  return (
                    <a key={x.id} href={x.url} target="_blank" rel="noopener noreferrer"
                      className="btn btn-outline-secondary" aria-label={x.label} title={x.label}
                      style={{ display: 'inline-flex', padding: 9, borderRadius: '50%', fontSize: 20, lineHeight: 1 }}>
                      <SocialIcon />
                    </a>
                  );
                })}
              </div>
            ) : (
              isSelf && <p className="text-muted">No links yet</p>
            )}
            {(isSelf || (bands && bands.length > 0)) && (
              <div className="mb-3">
                <strong>Bands</strong>
                {bands === null && <Spinner animation="border" size="sm" className="ml-2" />}
                {bands && bands.length === 0 && <div className="text-muted">No bands yet</div>}
                {bands && bands.map((b) => (
                  <div key={b.id} className="py-1">
                    <Link to={`/b/${encodeURIComponent(b.id)}`}>{b.name}</Link>
                    {b.members.length > 1 && <small className="text-muted ml-2">{b.members.length} members</small>}
                  </div>
                ))}
                {isSelf && (
                  <Form onSubmit={startBand} className="d-flex mt-2">
                    <Form.Control placeholder="New band name" aria-label="New band name" maxLength={MAX_NAME}
                      value={newBand} onChange={(e) => setNewBand(e.target.value)} />
                    <Button type="submit" variant="outline-primary" className="ml-2 flex-shrink-0"
                      disabled={creatingBand || !newBand.trim()}>Create band</Button>
                  </Form>
                )}
              </div>
            )}
            {isSelf && <p>
              <strong>Email</strong>{' '}
              {emailStatus && (emailStatus.hasEmail
                ? <Badge variant="success">Verified</Badge>
                : emailStatus.pending
                  ? <Badge variant="warning">Waiting for verification</Badge>
                  : <span className="text-muted">None, so you can't reset your password</span>)}{' '}
              {emailStatus && (
                <Button variant="link" size="sm" className="p-0 align-baseline"
                  onClick={() => { setEmailForm({ email: '', password: '' }); setShowEmail(true); }}>
                  {emailStatus.hasEmail || emailStatus.pending ? 'Change' : 'Add email'}
                </Button>
              )}
            </p>}
            {isSelf && (
              <React.Fragment>
                <Form.Check custom type="switch" id="profile-dark-mode" label="Night Mode UI" className="mb-3"
                  checked={dark} onChange={(e) => { setDark(e.target.checked); setDarkMode(e.target.checked); }} />
                <div className="mb-3">
                  <Form.Check custom type="switch" id="profile-discord" label="Show what I'm playing on Discord"
                    checked={discordOn} onChange={(e) => { setDiscordOn(e.target.checked); setPresenceEnabled(e.target.checked); }} />
                  {discordOn && (
                    <small className="text-muted">
                      {discordStatus === 'connected'
                        ? 'Connected to the Stew for Discord app on this computer.'
                        : 'Looking for the Stew for Discord app on this computer. Open it (soup bowl in the menu bar) and keep Discord open.'}
                    </small>
                  )}
                </div>
                {EXPERIMENTS.length > 0 && <div className="mb-3">
                  <div className="mb-1"><strong>Experimental</strong> <Badge variant="info">Beta</Badge></div>
                  {EXPERIMENTS.map((x) => (
                    <div key={x.id} className="mb-2">
                      <Form.Check custom type="switch" id={`profile-experiment-${x.id}`} label={x.label}
                        checked={!!experiments[x.id]}
                        onChange={(e) => { setExperiment(x.id, e.target.checked); setExperiments(getExperiments()); }} />
                      <small className="text-muted">{x.description} Applies to this browser only.</small>
                    </div>
                  ))}
                </div>}
                <Button variant="outline-primary" onClick={startEditing}>Edit profile</Button>{' '}
                <Button variant="outline-secondary" onClick={signOut}>Sign out</Button>
              </React.Fragment>
            )}
          </div>
        )}

        {editing && (
          <div className="mt-5 pt-3 border-top">
            <Button variant="outline-danger" onClick={() => { setDeleteConfirm(''); setShowDelete(true); }}>Delete account</Button>
          </div>
        )}
      </div>

      {cropFile && (
        <PfpCropper file={cropFile} saving={uploading === 'pfp'} onSave={savePfp} onCancel={() => setCropFile(null)} />
      )}

      <Modal show={showEmail} onHide={() => setShowEmail(false)} centered>
        <Form onSubmit={saveEmail}>
          <Modal.Header closeButton><Modal.Title>Email address</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="text-muted">We'll send a link to confirm it. Password resets go to a confirmed address.</p>
            <Form.Group controlId="profile-email">
              <Form.Label>Email</Form.Label>
              <Form.Control autoFocus type="email" autoCapitalize="none" value={emailForm.email}
                onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} />
            </Form.Group>
            <Form.Group controlId="profile-email-password">
              <Form.Label>Current password</Form.Label>
              <Form.Control type="password" autoComplete="current-password" value={emailForm.password}
                onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmail(false)}>Cancel</Button>
            <Button type="submit" disabled={emailSaving || !emailForm.email || !emailForm.password}>
              {emailSaving ? 'Sending…' : 'Send verification'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton><Modal.Title>Delete account</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>This permanently deletes your account. Type <strong>{username}</strong> to confirm.</p>
          <Form.Control autoFocus value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button variant="danger" disabled={deleteConfirm !== username} onClick={deleteAccount}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Profile;
